import { useEffect, useState } from 'react';
import { generateRandomId } from './generateRandomId';

/**
 * Gets an id for the overlay mount node based on the prefix.
 */
function getSafeId(prefix?: string) {
  const safePrefix = !prefix ? 'global' : prefix.toLowerCase().replace(/[^a-z0-9]/g, '');
  return `${safePrefix}-tanium-overlay-mount`;
}

/**
 * Creates or retrieves a DOM node used for displaying overlays. This node is appended to the body
 * element and should trump virtually all stacking contexts.
 *
 * @param prefix the prefix to apply to the node id
 */
function getOrCreateOverlayMountNode(prefix?: string) {
  const id = getSafeId(prefix);
  const existingOverlayNode = document.getElementById(id);

  if (existingOverlayNode !== null) {
    return existingOverlayNode;
  }

  const newOverlayNode = document.createElement('div');
  newOverlayNode.id = id;
  document.body.appendChild(newOverlayNode);

  return newOverlayNode;
}

/**
 * Removes the overlay mount node for a given prefix.
 * @param prefix the prefix to apply to the node id
 */
function removeOverlayMountNode(prefix?: string) {
  const id = getSafeId(prefix);
  const existingOverlayNode = document.getElementById(id);
  if (existingOverlayNode) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    existingOverlayNode.parentNode!.removeChild(existingOverlayNode);
  }
}

export function useOverlayMountNode() {
  // set to placeholder element initially because uses of this hook assume an element is always returned
  // but useEffect won't set the element until a second (immediate) render cycle.
  const [mountNode, setMountNode] = useState<HTMLElement>(document.createElement('div'));

  useEffect(() => {
    const id = generateRandomId();
    const newMountNode = getOrCreateOverlayMountNode(id);
    setMountNode(newMountNode);

    return () => {
      removeOverlayMountNode(id);
    };
  }, []);

  return mountNode;
}
